import React, { ChangeEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../reducers/store';
import { postValidateFirstName, postValidateLastName, postValidatePhoneNumber, postValidateSex } from '../../validations/enrollment/enrollment';
import { actionFetchEnrollmentUser, actionUpdateEnrollment } from '../../../actions/enrollment';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import {Button, FormField, Loader, SubCover, SubmitModal} from '../..';

import './editEnrollmentDetails.css'
import { genderOptions } from '../../../utils/constants';
import { Create } from '@mui/icons-material';

interface PostDataParams {
  firstName?: string;
  lastName?: string;
  sex?: string;
  phoneNumber?: string;
}

const EditEnrollmentDetails = ({enrollmentData}: any) => {
  const { id }                                          = useParams();
  const dispatch                                        = useDispatch<AppDispatch>();
  const [savingInfo, setSavingInfo]                     = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled]         = useState<boolean>(true);
  const [onOpen, setOnOpen]                             = useState(false);
  const [openSubMegaMenu, setOpenSubMegaMenu]           = useState<boolean>(false);
  const [errorMessage, setErrorMessage]                 = useState(null);
  const [firstNameErrors, setFirstNameErrors]           = useState<string[]>([]);
  const [lastNameErrors, setLastNameErrors]             = useState<string[]>([]);
  const [sexErrors, setSexErrors]                       = useState<string[]>([]);
  const [phoneNumberErrors, setPhoneNumberErrors]       = useState<string[]>([]);
  const { singleEnrollmentUser }                        = useSelector((state: RootState) => state?.enrollmentList);
  
  const [postData, setPostData]           = useState<PostDataParams>({ firstName: enrollmentData?.firstName || '', lastName: enrollmentData?.lastName || '', sex: enrollmentData?.sex || '', phoneNumber: enrollmentData?.phoneNumber || '' })
  const [ isLoadingBtn, setIsLoadingBtn ] = useState({ firstName: false, lastName: false, sex: false, phoneNumber: false, resume: false})
  const [ isValid, setIsValid ]           = useState({ firstName: false, lastName: false, sex: false, phoneNumber: false, resume: false})
  
  const useDebounce = <T,>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
    const handler = setTimeout(() => {
        setDebounced(value);
    }, delay);
    return () => clearTimeout(handler);
    }, [value, delay]);

    return debounced;
  };
  
  const debouncedPostData = useDebounce<PostDataParams>(postData, 500);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIsLoadingBtn((prevState) => ({ ...prevState, [name]: true }));
    setPostData((prevState) => ({ ...prevState, [name]: value }));
  }

  useEffect(() => {
    const validateFirstName = () => {
      const { isValid, errors } = postValidateFirstName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        firstName: isValid,
      }));
      setFirstNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        firstName: false,
      }));
      return errors;
    };

    const validateLastName = () => {
      const { isValid, errors } = postValidateLastName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        lastName: isValid,
      }));
      setLastNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        lastName: false,
      }));
      return errors;
    };

    const validateSex = () => {
      const { isValid, errors } = postValidateSex({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        sex: isValid,
      }));
      setSexErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        sex: false,
      }));
      return errors;
    };

    const validatePhoneNumber = () => {
      const { isValid, errors } = postValidatePhoneNumber({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        phoneNumber: isValid,
      }));
      setPhoneNumberErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        phoneNumber: false,
      }));
      return errors;
    };
    
    const firstNameErrors        = validateFirstName();
    const lastNameErrors         = validateLastName();
    const sexErrors              = validateSex();
    const phoneNumberErrors      = validatePhoneNumber();

    setFirstNameErrors(firstNameErrors);
    setLastNameErrors(lastNameErrors);
    setSexErrors(sexErrors);
    setPhoneNumberErrors(phoneNumberErrors);

    const hasFormErrors = firstNameErrors.length > 0 || lastNameErrors.length > 0 || sexErrors.length > 0 || phoneNumberErrors.length > 0;

    setIsButtonDisabled(hasFormErrors);
  }, [debouncedPostData]);

  const handleModalSubmit = () => {
    setOnOpen(true)
    setOpenSubMegaMenu(true);
  }
  
  const handleCloseCover = () => {
    setOnOpen(false)
    setOpenSubMegaMenu(false);
  }

  const handleSubmit = async(e: ChangeEvent<any>) => {
    e.preventDefault();
    handleModalSubmit()
  }

  const cancelPostCreation = () => {
    setOnOpen(false); // Close the modal
    setSavingInfo(false);
    setOpenSubMegaMenu(false);
  };

  useEffect(() => {
  if (singleEnrollmentUser) {
    setPostData({
      firstName: singleEnrollmentUser.firstName || "",
      lastName: singleEnrollmentUser.lastName || "",
      sex: singleEnrollmentUser.sex || "",
      phoneNumber: singleEnrollmentUser.phoneNumber || ""
    });
  }
}, [singleEnrollmentUser]);

  const confirmPostCreation = async() => {
    setSavingInfo(true);
    setIsButtonDisabled(true)
    const res = await dispatch(actionUpdateEnrollment({id, postData}))
    try{
      if (res.status === 200) {
        // Refresh Redux data so UI updates immediately
        await dispatch(actionFetchEnrollmentUser({id}));
        setErrorMessage(null); // Clear any previous error message
        setSavingInfo(false)
        setOpenSubMegaMenu(false);
        toast.success("Enrollment successfully sent!");
      } else if(res.status === 400) {
        setErrorMessage(res.data.errorMessage);
        setSavingInfo(false)
        setOpenSubMegaMenu(false);
      }
    }catch(error){
      setErrorMessage(res.status)
      setSavingInfo(false)
      setOpenSubMegaMenu(false);
    }
    setOnOpen(false); // Close the modal after confirmation
  };

  return (
    <>
      <form onSubmit={handleSubmit} autoComplete="off">
        <FormField inputType type="text" labelName="First Name" name="firstName" value={postData.firstName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.firstName} isValid={isValid.firstName} errors={firstNameErrors || []} />

        <FormField inputType type="text" labelName="Last Name" name="lastName" value={postData.lastName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.lastName} isValid={isValid.lastName} errors={lastNameErrors || []} />

        <FormField selectType labelName="Gender" name="sex" value={postData.sex} handleChange={handleChange} options={genderOptions} isLoadingBtn={isLoadingBtn.sex} isValid={isValid.sex} errors={sexErrors || []} />

        <FormField inputType type="text" labelName="Phone Number" name="phoneNumber" maxLength={13} value={postData.phoneNumber} handleChange={handleChange} isLoadingBtn={isLoadingBtn.phoneNumber} isValid={isValid.phoneNumber} errors={phoneNumberErrors || []} />

        <SubmitModal onOpen={onOpen} onClose={cancelPostCreation} onConfirm={confirmPostCreation} modalImage={Create} onClickName="Submit" prompt="Are you sure to submit enrollment?" />

        <Button onClickButton onClick={handleModalSubmit} buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} Sending...</> : "Update Enrollment"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />

        {errorMessage && <p className='error-msg'>{errorMessage}</p>}
      </form>

      {openSubMegaMenu && (
        //z-index: 3, others should be higher
        <SubCover handleCloseCover={handleCloseCover} />
      )}
    </>
  )
}

export default EditEnrollmentDetails